import { useState, useMemo, useEffect } from 'react';
import { Shield, Search, Filter, ChevronDown, ChevronUp, Plus } from 'lucide-react';
import { ComingSoonWatermark } from '@/components/ComingSoonWatermark';
import { riskRegisterData, RiskSeverity, RiskStatus, RiskCategory } from '@/data/mockData';
import { Button } from '@/components/ui/button';

interface RiskCategoryData {
  id: string;
  name: string;
  description: string;
  color: string;
  icon: string;
  order: number;
  isActive: boolean;
}

type SortField = 'id' | 'severity' | 'status' | 'last_updated';
type SortDirection = 'asc' | 'desc';

export const RiskRegister: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [severityFilter, setSeverityFilter] = useState<RiskSeverity | 'All'>('All');
  const [statusFilter, setStatusFilter] = useState<RiskStatus | 'All'>('All');
  const [categoryFilter, setCategoryFilter] = useState<RiskCategory | 'All'>('All');
  const [sortField, setSortField] = useState<SortField>('severity');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [riskCategories, setRiskCategories] = useState<RiskCategoryData[]>([]);

  useEffect(() => {
    loadRiskCategories();
  }, []);

  const loadRiskCategories = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/risk-categories`);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setRiskCategories(data.data);
        }
      }
    } catch (err) {
      console.error('Error loading risk categories:', err);
    }
  };

  const getSeverityColor = (severity: RiskSeverity) => {
    switch (severity) {
      case 'Critical':
        return 'text-danger-500 bg-danger-500/20 border-danger-500/30';
      case 'High':
        return 'text-warning-500 bg-warning-500/20 border-warning-500/30';
      case 'Medium':
        return 'text-primary-400 bg-primary-500/20 border-primary-500/30';
      case 'Low':
        return 'text-success-400 bg-success-500/20 border-success-500/30';
    }
  };

  const getStatusColor = (status: RiskStatus) => {
    switch (status) {
      case 'Open':
        return 'text-warning-400 bg-warning-500/20 border-warning-500/30';
      case 'Mitigated':
        return 'text-primary-400 bg-primary-500/20 border-primary-500/30';
      case 'Closed':
        return 'text-success-400 bg-success-500/20 border-success-500/30';
    }
  };

  const getSeverityWeight = (severity: RiskSeverity): number => {
    const weights = { 'Critical': 4, 'High': 3, 'Medium': 2, 'Low': 1 };
    return weights[severity];
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const filteredAndSortedRisks = useMemo(() => {
    let filtered = riskRegisterData.filter((risk) => {
      const matchesSearch = 
        risk.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        risk.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        risk.owner.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesSeverity = severityFilter === 'All' || risk.severity === severityFilter;
      const matchesStatus = statusFilter === 'All' || risk.status === statusFilter;
      const matchesCategory = categoryFilter === 'All' || risk.category === categoryFilter;

      return matchesSearch && matchesSeverity && matchesStatus && matchesCategory;
    });

    // Sort
    filtered.sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortField) {
        case 'severity':
          aValue = getSeverityWeight(a.severity);
          bValue = getSeverityWeight(b.severity);
          break;
        case 'last_updated':
          aValue = new Date(a.last_updated).getTime();
          bValue = new Date(b.last_updated).getTime();
          break;
        default:
          aValue = a[sortField];
          bValue = b[sortField];
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [searchTerm, severityFilter, statusFilter, categoryFilter, sortField, sortDirection]);

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortField !== field) return <ChevronDown className="w-4 h-4 text-neutral-600" />;
    return sortDirection === 'asc' 
      ? <ChevronUp className="w-4 h-4 text-primary-400" />
      : <ChevronDown className="w-4 h-4 text-primary-400" />;
  };

  return (
    <div className="space-y-6">
      <ComingSoonWatermark 
        pageKey="risk-register"
        icon={Shield}
        title="Risk Register"
        description="Comprehensive risk management and tracking"
      />

      {/* Risk Categories Section */}
      <div className="bg-neutral-950 rounded-lg border border-neutral-800 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-white">Risk Categories</h2>
            <p className="text-sm text-neutral-400 mt-1">
              Manage top-level risk categories for your organization
            </p>
          </div>
          <Button className="bg-neutral-800 hover:bg-neutral-700 text-white">
            <Plus className="w-4 h-4 mr-2" />
            Add Category
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {riskCategories.map((category) => (
            <div
              key={category.id}
              className="bg-neutral-900 border border-neutral-800 rounded-lg p-4 hover:border-neutral-700 transition-colors cursor-pointer"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-semibold text-white">{category.name}</h3>
                  <p className="text-xs text-neutral-400 mt-1">{category.description}</p>
                </div>
                <div 
                  className="w-8 h-8 rounded flex items-center justify-center" 
                  style={{ backgroundColor: category.color + '20' }}
                >
                  <div className="w-4 h-4" style={{ color: category.color }}>●</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Risk Register Table */}
      <div className="card">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center">
              <Shield className="w-8 h-8 text-primary-400 mr-3" />
              Risk Register
            </h1>
            <p className="text-neutral-400 mt-1">Comprehensive view of organizational risks</p>
          </div>
        </div>

      {/* Filters and Search */}
      <div className="bg-neutral-950 rounded-lg border border-neutral-800 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400" />
              <input
                type="text"
                placeholder="Search risks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-neutral-900 border border-neutral-700 rounded-lg text-white placeholder-neutral-500 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
              />
            </div>
          </div>

          {/* Severity Filter */}
          <div>
            <select
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value as RiskSeverity | 'All')}
              className="w-full px-4 py-2 bg-neutral-900 border border-neutral-700 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            >
              <option value="All">All Severities</option>
              <option value="Critical">Critical</option>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as RiskStatus | 'All')}
              className="w-full px-4 py-2 bg-neutral-900 border border-neutral-700 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            >
              <option value="All">All Statuses</option>
              <option value="Open">Open</option>
              <option value="Mitigated">Mitigated</option>
              <option value="Closed">Closed</option>
            </select>
          </div>

          {/* Category Filter */}
          <div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value as RiskCategory | 'All')}
              className="w-full px-4 py-2 bg-neutral-900 border border-neutral-700 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            >
              <option value="All">All Categories</option>
              <option value="Operational">Operational</option>
              <option value="Financial">Financial</option>
              <option value="Strategic">Strategic</option>
              <option value="Compliance">Compliance</option>
              <option value="Technology">Technology</option>
              <option value="Cybersecurity">Cybersecurity</option>
              <option value="Reputational">Reputational</option>
            </select>
          </div>
        </div>

        {/* Active Filters Summary */}
        <div className="mt-4 flex items-center gap-2 text-sm text-neutral-400">
          <Filter className="w-4 h-4" />
          <span>
            Showing {filteredAndSortedRisks.length} of {riskRegisterData.length} risks
          </span>
        </div>
      </div>

      {/* Risk Table */}
      <div className="bg-neutral-950 rounded-lg border border-neutral-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-neutral-900 border-b border-neutral-800">
                <th 
                  className="text-left px-6 py-4 text-sm font-semibold text-neutral-300 cursor-pointer hover:bg-neutral-800 transition-colors"
                  onClick={() => handleSort('id')}
                >
                  <div className="flex items-center gap-2">
                    Risk ID
                    <SortIcon field="id" />
                  </div>
                </th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-neutral-300">
                  Title
                </th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-neutral-300">
                  Category
                </th>
                <th 
                  className="text-left px-6 py-4 text-sm font-semibold text-neutral-300 cursor-pointer hover:bg-neutral-800 transition-colors"
                  onClick={() => handleSort('severity')}
                >
                  <div className="flex items-center gap-2">
                    Severity
                    <SortIcon field="severity" />
                  </div>
                </th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-neutral-300">
                  Owner
                </th>
                <th 
                  className="text-left px-6 py-4 text-sm font-semibold text-neutral-300 cursor-pointer hover:bg-neutral-800 transition-colors"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center gap-2">
                    Status
                    <SortIcon field="status" />
                  </div>
                </th>
                <th 
                  className="text-left px-6 py-4 text-sm font-semibold text-neutral-300 cursor-pointer hover:bg-neutral-800 transition-colors"
                  onClick={() => handleSort('last_updated')}
                >
                  <div className="flex items-center gap-2">
                    Last Updated
                    <SortIcon field="last_updated" />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedRisks.map((risk) => (
                <tr 
                  key={risk.id} 
                  className="border-b border-neutral-800 hover:bg-neutral-900 transition-colors cursor-pointer"
                  onClick={() => console.log('View risk:', risk.id)}
                >
                  <td className="px-6 py-4">
                    <span className="font-mono text-sm text-primary-400">{risk.id}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-white font-medium">{risk.title}</p>
                      {risk.description && (
                        <p className="text-xs text-neutral-400 mt-1 line-clamp-1">{risk.description}</p>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-neutral-300">{risk.category}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded text-xs font-medium border ${getSeverityColor(risk.severity)}`}>
                      {risk.severity}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-neutral-300">{risk.owner}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded text-xs font-medium border ${getStatusColor(risk.status)}`}>
                      {risk.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-neutral-400">{risk.last_updated}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {filteredAndSortedRisks.length === 0 && (
          <div className="text-center py-12">
            <Shield className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
            <p className="text-neutral-400">No risks found matching your filters</p>
            <button 
              onClick={() => {
                setSearchTerm('');
                setSeverityFilter('All');
                setStatusFilter('All');
                setCategoryFilter('All');
              }}
              className="mt-4 px-4 py-2 bg-primary-500/20 text-primary-400 border border-primary-500/30 rounded-lg hover:bg-primary-500/30 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>
      </div>
    </div>
  );
};
